import { Component } from 'react';
import ReactDOM from 'react-dom';
import './Modal.css';
import classNames from 'classnames';
import SvgAF from 'components/shared/SvgAF/SvgAF';
import { Provider, useStore } from 'react-redux';
import { Router, useHistory } from 'react-router-dom';
import { useQueryClient, QueryClientProvider } from '@tanstack/react-query';
import { IconLoader } from 'common/IconLoader';

import ReactTooltip from 'react-tooltip';

export const ModalStates = {
  OPEN: 0,
  CLOSED: 1,
  CLOSING: 2
};

/*
    props: 
        closeTargetRef - A component ref which the modal will animate towards on close
        close - function to run when modal is done closing
        closing - set this to true to close the modal from a parent
        animate - whether the modal animates when a parent closes it
        animateX - whether the modal animates when its x button is clicked
        zIndex - a value greater than 0 will place it on top of lower zIndexed modals
*/
class ModalImpl extends Component {
  constructor(props) {
    super(props);
    this.onClose = this.onClose.bind(this);
    this.onCloseNoAnim = this.onCloseNoAnim.bind(this);
    this.resize = this.resize.bind(this); //need to bind this so we can remove listener on unmount

    this.state = {
      closing: false
    };
  }

  resize() {
    this.setState({ resize: true }); //this is a dummy variable to force rerender
  }

  onClose() {
    this.backgroundRef.classList.add('Close_Anim');
    this.contentRef.classList.add('Close_Anim');
    let rect = this.props.closeTargetRef.getBoundingClientRect();
    let xTarget = Math.floor(rect.left + rect.width / 2 + window.pageXOffset);
    let yTarget = Math.floor(rect.top + rect.height / 2 + window.pageYOffset);

    console.log('left string:', xTarget.toString() + 'px', '\ntop string:', yTarget.toString() + 'px');
    this.contentRef.style.left = xTarget.toString() + 'px';
    this.contentRef.style.top = yTarget.toString() + 'px';
    this.contentRef.style.margin = '0px 0px';
    this.contentRef.style.minWidth = '0px';
    this.contentRef.style.minHeight = '0px';
    this.contentRef.style.height = '1px';
    this.contentRef.style.width = '1px';

    this.setState({ closing: true });
    setTimeout(this.props.close, 500);
  }
  onCloseNoAnim() {
    this.setState({ closing: true });
    this.props.close();
  }

  componentDidMount() {
    document.documentElement.style.overflow = 'hidden';
    window.addEventListener('resize', this.resize);
    ReactTooltip.hide();
    ReactTooltip.rebuild();
    if (this.props.global) {
      //Do this in update/mount
      ReactDOM.render(this.renderGlobalModal(), document.getElementById('GlobalModalContainer'));
    }
  }

  componentWillUnmount() {
    document.documentElement.style.overflow = 'auto hidden';
    ReactTooltip.rebuild();
    if (this.props.global) {
      const container = document.getElementById('GlobalModalContainer');
      if (container) {
        ReactDOM.unmountComponentAtNode(container);
      }
    }

    // window.addEventListener('resize', this.resize);
    window.removeEventListener('resize', this.resize);
  }

  componentDidUpdate() {
    if (this.props.closing && !this.state.closing) {
      if (this.props.animate) {
        this.onClose();
      } else {
        this.onCloseNoAnim();
      }
    }
    if (this.props.global) {
      //Do this in update/mount
      ReactDOM.render(this.renderGlobalModal(), document.getElementById('GlobalModalContainer'));
    }
  }

  renderModal() {
    let height = window.innerHeight;
    let width = '100vw'; //window.document.body.offsetWidth;
    //this.width = Math.floor(window.innerWidth / 2);
    //this.height = Math.floor(window.innerHeight / 2);
    //this.top = Math.floor(window.pageYOffset + window.innerHeight / 4);
    //this.top = Math.floor(window.pageYOffset + (window.innerHeight / 4);
    //this.left = Math.floor(window.pageXOffset + window.innerWidth / 4);

    let button =
      !this.state.closing && !this.props.closeXHidden ? (
        <button
          type="button"
          aria-label="Close"
          className={'Modal_X'}
          onClick={this.props.animateX ? this.onClose : this.onCloseNoAnim}
        >
          <SvgAF img={IconLoader.icon_close_x} hover={IconLoader.icon_close_x_hover} />
        </button>
      ) : null;
    let opaque = true;
    if (this.props.opaque !== null && this.props.opaque === false) {
      opaque = false;
    }

    let contentHeight = 300;
    if (this.props.height != null) {
      contentHeight = this.props.height;
    }
    let contentWidth = 360;
    if (this.props.width != null) {
      contentWidth = this.props.width;
    }

    this.top = Math.floor(window.pageYOffset + (window.innerHeight - contentHeight) / 2);

    if (this.top < 0) this.top = 0;

    let heightString = Number.isInteger(contentHeight) ? contentHeight + 'px' : contentHeight;
    let widthString = Number.isInteger(contentWidth) ? contentWidth + 'px' : contentWidth;
    let contentDivStyle = { height: heightString, width: widthString, top: this.top + 'px' };
    let zIndex = 10000;

    if (this.props.zIndex) {
      zIndex += this.props.zIndex; //for layering modals on top of others
    }

    return (
      <div
        id="ActiveModal"
        role={this.props.role || 'dialog'}
        aria-label={this.props['aria-label']}
        aria-labelledby={this.props['aria-labelledby']}
        aria-describedby={this.props['aria-describedby']}
        className={classNames({
          Modal_BackgroundDiv: true,
          Modal_BackgroundDiv_Clear: !opaque,
          Modal_BackgroundDiv_Flex: this.props.displayFlex,
          'scroll-component': true
        })}
        ref={(ref) => {
          this.backgroundRef = ref;
        }}
        style={{ height: height, width: width, zIndex: zIndex }}
      >
        <div
          className={classNames({
            Modal_ContentDiv: true,
            overflow: this.props.overflow,
            noMinWidth: this.props.noMinWidth
          })}
          ref={(ref) => {
            this.contentRef = ref;
          }}
          style={contentDivStyle}
        >
          {this.props.hideCloseButton === true ? null : <div className="Modal_X_Div">{button}</div>}
          <div
            className={classNames({
              [this.props.className ? `${this.props.className}` : 'Modal_InnerContentDiv']: true,
              noPadding: this.props.noPadding,
              overflowY: this.props.overflowY
            })}
            style={{ backgroundColor: this.props.colorize }}
          >
            {!this.state.closing ? this.props.children : null}
          </div>
        </div>
      </div>
    );
  }
  renderGlobalModal() {
    return (
      <QueryClientProvider client={this.props.queryClient}>
        <Provider store={this.props.store}>
          <Router history={this.props.history}>{this.renderModal()}</Router>
        </Provider>
      </QueryClientProvider>
    );
  }

  render() {
    if (this.props.global) {
      //Do this in update/mount
      //ReactDOM.render(wholeModal, document.getElementById('GlobalModalContainer'));
    } else {
      return this.renderModal();
    }

    return null;
  }
}

/**
 * @deprecated try to use new Modal or Dialog
 */
export default function Modal(props) {
  const queryClient = useQueryClient();
  const history = useHistory();
  const store = useStore();

  return <ModalImpl {...props} history={history} store={store} queryClient={queryClient} />;
}
